/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import { fetchWithTimeout } from '../../fetch/fetch';
import HeadersUtil from '../../headers/headers-util';
import RequestUtil from '../../request/request-util';

/**
 * Výchozí hlavičky pro HTTP požadavky
 *
 * Tyto hlavičky se používají, aby se zabránilo vrácení odpovědi z cache.
 * Velké soubory nejsou příliš vhodné k ukládání do cache
 * a nelze zaručit přítomnost cache na všech platformách.
 *
 * Hlavičky se vkládají automaticky, protože by bez nich stahování souborů
 * nefungovalo správně.
 * Např. by mohlo dojít k tomu, že multiplatformní HEAD dotazy by
 * se cachovaly, ale platformově specifické stahování binárních souborů
 * by cachováno nebylo.
 * Obdobným způsobem přidává vybrané hlavičky i Fetch API.
 * Uživatel může hlavičky také nastavit, ale pouze na tyto výchozí hodnoty.
 * @private
 * @type {Object.<string, string>}
 */
const defaultHeaders = {
  'Cache-Control': 'no-store',
  Pragma: 'no-cache'
};

/**
 * Seznam omezení na `Request` a `RequestInit`
 *
 * Obsahuje mapu vlastností u kterých jsou vyžadované konkrétní hodnoty.
 * Klíčem v mapě je název položky v `Request` a `RequestInit`.
 * Některé položky mohou ve staších verzích API chybět,
 * proto nejsou označené jako `required`.
 *
 * Položka `body` se nevaliduje, protože je to složitější.
 * V `RequestInit` musí být `undefined`,
 * ale výsledkem `Request.text()` je `""`.
 * Navíc tyto hodnoty nelze zaměnit.
 * Validaci `body` obstarává fetch knihovna,
 * která nedovoluje nastavení těla u metod GET a HEAD.
 *
 * Položka `signal` se nevaliduje, i když na ni také existuje omezení.
 * Validace totiž není s aktuálním Fetch API možné.
 * V `RequestInit` může být undefined, ale v `Request` už ne.
 * Navíc z `Request#signal` nelze poznat, že je neaktivní.
 * Jediným řešením by asi byla vlastní implementace `Request`.
 * Zatím se tedy ignoruje.
 *
 * Hodnotu položky `window` nelze z již vytvořeného Request vyčíst.
 * Ale vzhledem k tomu, že může být pouze `undefined` nebo `null`,
 * tak by měla být její hodnota po nastavení výchozí na `null` zaručena.
 * @private
 * @type {Object.<string, RequestConstraint>}
 */
const requestConstraints = {
  cache: { value: 'default' },
  credentials: { required: true, value: 'omit' },
  integrity: { value: '' },
  keepalive: { value: false },
  method: { required: true, value: 'GET' },
  mode: { required: true, value: 'cors' },
  redirect: { value: 'follow' },
  // #501 - Nefunkční stahování souborů - chyba validace referrer
  // Novější browsery akceptují a nepřepisují hodnotu '', padá však na Chromiu 41.
  // about:client je univerzální hodnota pro Chromium kolem verze 41 a novější.
  referrer: { required: true, value: 'about:client' },
  referrerPolicy: { value: 'no-referrer' },
  window: { value: null }
};

/**
 * Utility pro přenosy souborů po síti
 */
export default class FileTransferUtil {
  /**
   * Vytvoří GET požadavek pro stažení souboru.
   * Na požadavku se aplikují specifické výchozí hodnoty a pak se zkontroluje,
   * že jsou všechna nastavení podporovaná.
   * @param {Request|string} input
   * @param {RequestInit} [init]
   * @returns {Request}
   */
  static createGetRequest(input, init) {
    const request = FileTransferUtil.initRequest(input, init);
    const { message, valid } = FileTransferUtil.validateRequest(request);
    if (!valid) {
      throw new TypeError(`Failed to download: ${message}`);
    }
    return request;
  }

  /**
   * Vytvoří HEAD požadavek pro stažení souboru.
   * HEAD požadavek se vytváří zkopírováním GET požadavku a nastavením
   * HTTP metody na HEAD.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Request} getRequest
   * @returns {Promise.<Request>}
   */
  static createHeadRequest(getRequest) {
    return new Promise((resolve) =>
      resolve(
        RequestUtil.toInit(getRequest).then((getInit) => {
          const headInit = { ...getInit, method: 'HEAD' };
          return new Request(getRequest.url, headInit);
        })
      )
    );
  }

  /**
   * Vytvoří se odpověď 304 `Not Modified`, pro případ, že by server odpověděl
   * statusem v rozsahu 200 - 299, ale obsah hlaviček by tomu neodpovídal.
   * @param {Headers} okResponseHeaders
   * @private
   * @returns {Response}
   */
  static createNotModifiedResponse(okResponseHeaders) {
    return new Response(undefined, {
      status: 304,
      headers: HeadersUtil.toObject(okResponseHeaders)
    });
  }

  /**
   * Odešle HEAD požadavek před stažení souboru.
   * Ošetří se možné chyby serveru při zpracování hlaviček `If-None-Match`
   * a `If-Modified-Since` tím, že se provede jejich kontrola.
   * Dále se ošetří případné selhání požadavku pro případ,
   * že by HEAD požadavek neuspěl, např. z důvodu CORS omezení.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Request} headRequest
   * @returns {Promise.<Response>}
   */
  static fetchHeadBeforeDownload(headRequest) {
    return new Promise((resolve) =>
      resolve(
        fetchWithTimeout(headRequest).then((response) => {
          // U statusů mimo rozsah 200 - 299 se nemusí kontrola hlaviček dělat.
          if (!response.ok) {
            return response;
          }
          // Kontrola hlavičky `If-None-Match`
          const requestHeaders = headRequest.headers;
          const responseHeaders = response.headers;
          const ifNoneMatch = requestHeaders.get('If-None-Match');
          const etag = responseHeaders.get('ETag');
          if (!isNil(ifNoneMatch) && !isNil(etag)) {
            // TODO If-None-Match může být *, ale mělo by se to týkat jen PUT
            if (ifNoneMatch.includes(etag)) {
              return FileTransferUtil.createNotModifiedResponse(
                responseHeaders
              );
            }
            return response;
          }
          // Kontrola hlavičky `If-Modified-Since`
          const ifModifiedSince = requestHeaders.get('If-Modified-Since');
          const lastModified = responseHeaders.get('Last-Modified');
          if (!isNil(ifModifiedSince) && !isNil(lastModified)) {
            const ifModifiedSinceTime = new Date(ifModifiedSince).getTime();
            const lastModifiedTime = new Date(lastModified).getTime();
            // Pokud je v požadavku hlavička If-Modified-Since
            // a v odpovědi je hlavička Last-Modified,
            // tak se hodnoty těchto hlaviček porovnají.
            // Return 200 status, only if it has been last modified after
            // the modified since date
            if (
              !isNaN(ifModifiedSinceTime) &&
              !isNaN(lastModifiedTime) &&
              ifModifiedSinceTime >= lastModifiedTime
            ) {
              return FileTransferUtil.createNotModifiedResponse(
                responseHeaders
              );
            }
            return response;
          }
          return response;
        })
      )
    );
  }

  /**
   * Odešle HEAD požadavek po stažení souboru.
   * Ověřuje, že se hodnoty hlaviček `ETag` a `Last-Modified` nezměnili
   * od prvního HEAD požadavku, jinak by nebylo jasné,
   * které z nich patří ke staženému souboru.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Response} firstResponse
   * @param {Request} headRequest
   * @param {string} url
   * @returns {Promise.<Response>}
   */
  static fetchHeadAfterDownload(firstResponse, headRequest, url) {
    return new Promise((resolve) => {
      if (!firstResponse.ok) {
        return resolve(firstResponse);
      }
      const firstHeaders = firstResponse.headers;
      const firstEtag = firstHeaders.get('ETag');
      const hasEtag = !isNil(firstEtag);
      const firstLastModified = firstHeaders.get('Last-Modified');
      const hasLastModified = !isNil(firstLastModified);
      if (!hasEtag && !hasLastModified) {
        return resolve(firstResponse);
      }
      return resolve(
        // Z head požadavku se převezme init konfigurace.
        // URL se použije finální po přesměrování, které provedl první HEAD.
        RequestUtil.toInit(headRequest)
          .then((headInit) => fetchWithTimeout(url, headInit))
          .then((secondResponse) => {
            if (firstResponse.status !== secondResponse.status) {
              return new Response();
            }
            const secondHeaders = secondResponse.headers;
            const secondEtag = secondHeaders.get('ETag');
            const secondLastModified = secondHeaders.get('Last-Modified');
            if (
              (hasEtag && firstEtag !== secondEtag) ||
              (hasLastModified && firstLastModified !== secondLastModified)
            ) {
              return new Response();
            }
            return firstResponse;
          })
      );
    });
  }

  /**
   * Zjistí URL, která se má použít pro stažení souboru a druhý HEAD dotaz.
   * Preferuju se použití finální URL po všech přesměrováních.
   * To by mělo minimalizovat odlišnosti v přesměrování na různých platformách.
   * Také by to mělo minimalizovat možnost,
   * že se přesměrování mezi jednotlivými požadavky změní.
   * @param {Response} firstResponse
   * @param {Request} getRequest
   * @returns {string}
   */
  static getUrl(firstResponse, getRequest) {
    // Použití operátoru || je z důvodu, že zde může být řetězec
    // a teoreticky i null z XMLHttpRequest, který používá polyfill.
    // URL po přesměrování se tedy použije pouze, pokud je to neprázdný řetězec.
    return firstResponse.url || getRequest.url;
  }

  /**
   * Ošetří selhání při provádění HEAD dotazů.
   * Selhání se ignoruje, protože nelze zaručit 100% podporu pro HEAD dotazy,
   * především kvůli možnosti aplikace CORS omezení.
   * Také nelze rozeznat selhání kvůli CORS a kvůli jinému důvodu.
   * Výsledkem je v tom případě prázdná odpověď se statusem 200,
   * což je stejná reakce jako při původním stahování bez HEAD požadavků.
   * @param {Error} error
   * @param {import('../../logger/logger').Logger} logger
   * @returns {Response}
   */
  static handleFetchHeadError(error, logger) {
    logger.warn(error);
    return new Response();
  }

  /**
   * Vytvoří požadavek ze vstupů ve formátu pro fetch metodu.
   * Na požadavku se aplikují specifické výchozí hodnoty.
   * @param {Request|string} input
   * @param {RequestInit} [init]
   * @private
   * @returns {Request}
   */
  static initRequest(input, init) {
    if (isString(input)) {
      const initObject = init ?? {};
      const initDefaults = Object.fromEntries(
        Object.entries(requestConstraints).map(([key, { value }]) => [
          key,
          value
        ])
      );
      initDefaults.headers = defaultHeaders;
      const requestInit = RequestUtil.mergeInit(initDefaults, initObject);
      return new Request(input, requestInit);
    }
    if (!isNil(init)) {
      // TODO Podporovat Request + init dle specifikace
      throw new Error('Not implemented.');
    }
    return input;
  }

  /**
   * Zkontroluje, jestli jsou všechny hlavičky podporovány.
   * @param {Headers} headers
   * @private
   * @returns {ValidationResult}
   */
  static validateHeaders(headers) {
    const entries = Object.entries(defaultHeaders);
    const foundEntry = entries.find(
      ([key, value]) => headers.get(key) !== value
    );
    if (!isNil(foundEntry)) {
      const [key, value] = foundEntry;
      return {
        message: `Header ${key} is "${headers.get(
          key
        )}" instead of "${value}".`,
        valid: false
      };
    }
    return { valid: true };
  }

  /**
   * Zkontroluje, jestli jsou všechna nastavení požadavku podporována.
   * @param {Request} request
   * @private
   * @returns {ValidationResult}
   */
  static validateRequest(request) {
    const entries = Object.entries(requestConstraints);
    const foundEntry = entries.find(
      ([key, { required, value }]) =>
        !(request[key] === value || (!required && request[key] === undefined))
    );
    if (!isNil(foundEntry)) {
      const [key, values] = foundEntry;
      return {
        message: `Request ${key} is "${request[key]}" instead of "${values[0]}".`,
        valid: false
      };
    }
    return FileTransferUtil.validateHeaders(request.headers);
  }
}

/**
 * Omezení na `Request` a `RequestInit`
 * @private
 * @typedef {Object} RequestConstraint
 * @property {boolean} required Příznak, jestli musí mít `Request` tuto hodnotu
 * nastavenou.
 * @property {*} value Výchozí a zároveň jediná povolená hodnota pro danou
 * položku `Request` nebo `RequestInit`.
 */

/**
 * Výsledek validace `Headers` a `Request`
 * @private
 * @typedef {Object} ValidationResult
 * @property {string} [message] Chybové hlášení, které je vyplněné,
 * pokud objekt není validní.
 * @property {boolean} valid Příznak, jestli je objekt validní.
 */
